/**
 * Parcel-units normalisation — converts any locale-shape parcel block into
 * the canonical (grams + millimetres) form post-K.6.
 *
 * Storage is canonical (PLAN §A row "Parcel units", policy C, locked
 * 2026-06-22): `listings.parcel_weight_grams` + `parcel_{length,width,height}_mm`,
 * no unit columns. UI form in slice C1 still emits the legacy shape
 * (`parcel_weight` + `parcel_{length,width,height}` + `parcel_mass_unit` +
 * `parcel_distance_unit`); UI form refactor lands in slice C2.
 *
 * This module is the boundary helper that bridges the two shapes server-side.
 * Anchor question (`type-driven-design §7`): exactly one file owns the unit
 * → canonical conversion. Add a new mass unit → add a new arm here, no other
 * file changes.
 *
 * Conversion factors (NIST exact where possible):
 *   - oz → g  = ×28.3495
 *   - lb → g  = ×453.592
 *   - kg → g  = ×1000
 *   - in → mm = ×25.4
 *   - cm → mm = ×10
 *
 * @see .tondocs/shippo/PLAN.md §A row "Parcel units" (locked 2026-06-22)
 * @see .tondocs/shippo/PLAN.md §K.6 step 7
 */

export type MassUnit = "lb" | "oz" | "kg" | "g";
export type DistanceUnit = "in" | "cm" | "mm";

const MASS_TO_GRAMS: Readonly<Record<MassUnit, number>> = {
  g: 1,
  kg: 1000,
  oz: 28.3495,
  lb: 453.592,
};

const DISTANCE_TO_MM: Readonly<Record<DistanceUnit, number>> = {
  mm: 1,
  cm: 10,
  in: 25.4,
};

/**
 * Exact grams-per-unit factor, unrounded.
 *
 * `toGrams` rounds its RESULT, so it cannot be used to obtain a factor:
 * `toGrams(1, 'oz')` is 28, not 28.3495. Callers that need to divide in the
 * other direction (canonical → locale, e.g. the input codecs) must use this.
 */
export function gramsPerMassUnit(unit: MassUnit): number {
  return MASS_TO_GRAMS[unit];
}

/**
 * Exact millimetres-per-unit factor, unrounded. See `gramsPerMassUnit` for
 * why `toMm(1, 'in')` (= 25) is the wrong way to get one.
 */
export function mmPerDistanceUnit(unit: DistanceUnit): number {
  return DISTANCE_TO_MM[unit];
}

/**
 * Convert a mass value to grams. Round to integer (DB column is numeric but
 * gram precision is fine — sub-gram weight inputs from a kitchen scale
 * routinely come in at ±1 g).
 */
export function toGrams(value: number, unit: MassUnit): number {
  return Math.round(value * MASS_TO_GRAMS[unit]);
}

/**
 * Convert a distance value to millimetres. Round to integer (1 mm precision
 * covers every parcel use case — Shippo accepts integer mm).
 */
export function toMm(value: number, unit: DistanceUnit): number {
  return Math.round(value * DISTANCE_TO_MM[unit]);
}

// ---------------------------------------------------------------------------
// Listing-shape normalisers
// ---------------------------------------------------------------------------

/**
 * Input shape accepted from listing create/edit producers. Producers may
 * send EITHER the canonical (_grams / _mm) shape OR the legacy
 * (parcel_weight + parcel_mass_unit + parcel_distance_unit) shape. C1 accepts
 * both; C2 drops the legacy branch.
 */
export interface ListingParcelInput {
  parcel_weight_grams?: number | null;
  parcel_length_mm?: number | null;
  parcel_width_mm?: number | null;
  parcel_height_mm?: number | null;
  parcel_weight?: number | null;
  parcel_length?: number | null;
  parcel_width?: number | null;
  parcel_height?: number | null;
  parcel_mass_unit?: MassUnit | null;
  parcel_distance_unit?: DistanceUnit | null;
}

export interface CanonicalParcel {
  parcel_weight_grams: number | null;
  parcel_length_mm: number | null;
  parcel_width_mm: number | null;
  parcel_height_mm: number | null;
}

/**
 * Normalise a listing-create or listing-patch parcel block to canonical
 * grams + millimetres. Caller picks one of:
 *   - canonical shape present → pass through.
 *   - legacy shape present    → convert each scalar using the unit columns
 *                                that came alongside.
 *   - mixed                   → canonical wins (it's the SoT after K.6).
 *   - absent / all-null       → emit all nulls (legacy-mode listing).
 *
 * Returns ONLY the canonical 4-key block — callers pass it into the listings
 * INSERT/UPDATE row. The route layer drops the legacy keys after this call.
 */
export function normalizeListingParcel(
  input: ListingParcelInput,
): CanonicalParcel {
  const weight = pickGrams(input);
  const length = pickMm(
    input.parcel_length_mm,
    input.parcel_length,
    input.parcel_distance_unit,
  );
  const width = pickMm(
    input.parcel_width_mm,
    input.parcel_width,
    input.parcel_distance_unit,
  );
  const height = pickMm(
    input.parcel_height_mm,
    input.parcel_height,
    input.parcel_distance_unit,
  );
  return {
    parcel_weight_grams: weight,
    parcel_length_mm: length,
    parcel_width_mm: width,
    parcel_height_mm: height,
  };
}

function pickGrams(input: ListingParcelInput): number | null {
  if (input.parcel_weight_grams != null) return Math.round(input.parcel_weight_grams);
  if (input.parcel_weight != null && input.parcel_mass_unit != null) {
    return toGrams(input.parcel_weight, input.parcel_mass_unit);
  }
  // Legacy weight without explicit unit → assume grams (conservative default;
  // pre-K.6 callers that set parcel_weight without a unit were already a bug).
  if (input.parcel_weight != null) return Math.round(input.parcel_weight);
  return null;
}

function pickMm(
  canonical: number | null | undefined,
  legacy: number | null | undefined,
  legacyUnit: DistanceUnit | null | undefined,
): number | null {
  if (canonical != null) return Math.round(canonical);
  if (legacy != null && legacyUnit != null) return toMm(legacy, legacyUnit);
  if (legacy != null) return Math.round(legacy);
  return null;
}
